/**
 * Line items for offers, invoices and credit notes. Totals are computed
 * with the same shared money engine the API uses, so the live preview is
 * exactly what gets stored.
 */
import { useMemo, useState, type ChangeEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  LINE_ITEM_TYPES,
  calcDocumentTotals,
  type CatalogItemResponse,
  type CommercialLineResponse,
  type CommercialSettingsResponse,
  type CommercialTotalsResponse,
  type LineItemType,
} from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { InputText } from "@primereact/ui/inputtext";
import { Textarea } from "@primereact/ui/textarea";
import { apiGet } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { useUnits } from "../../lib/units.js";
import { Field } from "../../components/ui/Field.js";
import { SelectInput } from "../../components/ui/controls.js";
import { cx } from "../../components/ui/cx.js";
import { formatMoney } from "../shared.js";

export interface EditableLine {
  key: string;
  type: LineItemType;
  title: string;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  discountPercent: string;
  taxRate: string;
  catalogItemId?: string;
  sourceOfferItemId?: string;
}

let nextKey = 0;
const newKey = () => `line-${(nextKey += 1)}`;

export function useCommercialSettings() {
  return useQuery({
    queryKey: ["settings", "commercial"],
    queryFn: () =>
      apiGet<{ data: CommercialSettingsResponse }>("/settings/commercial").then(
        (response) => response.data,
      ),
    staleTime: 60_000,
  });
}

export function emptyLine(defaultTaxRate = "0"): EditableLine {
  return {
    key: newKey(),
    type: "work",
    title: "",
    description: "",
    quantity: "1",
    unit: "h",
    unitPrice: "",
    discountPercent: "",
    taxRate: defaultTaxRate,
  };
}

export function toEditableLines(
  items: readonly CommercialLineResponse[],
): EditableLine[] {
  return items.map((item) => ({
    key: newKey(),
    type: item.type as LineItemType,
    title: item.title,
    description: item.description ?? "",
    quantity: item.quantity,
    unit: item.unit,
    unitPrice: item.unitPrice,
    discountPercent: item.discountPercent ?? "",
    taxRate: item.taxRate,
    ...(item.catalogItemId ? { catalogItemId: item.catalogItemId } : {}),
    ...(item.sourceOfferItemId
      ? { sourceOfferItemId: item.sourceOfferItemId }
      : {}),
  }));
}

const isNumber = (value: string) => /^-?\d+(\.\d+)?$/.test(value.trim());

/** Payload for the API; drops blank rows. */
export function linesPayload(lines: readonly EditableLine[]) {
  return lines
    .filter((line) => line.title.trim())
    .map((line) => {
      const text = line.type === "text";
      return {
        type: line.type,
        title: line.title.trim(),
        ...(line.description.trim()
          ? { description: line.description.trim() }
          : {}),
        quantity: text ? "0" : line.quantity.trim() || "0",
        unit: line.unit || "pcs",
        unitPrice: text ? "0" : line.unitPrice.trim() || "0",
        discountPercent:
          !text && line.discountPercent.trim()
            ? line.discountPercent.trim()
            : null,
        taxRate: text ? "0" : line.taxRate || "0",
        catalogItemId: line.catalogItemId ?? null,
        sourceOfferItemId: line.sourceOfferItemId ?? null,
      };
    });
}

export function linesAreValid(lines: readonly EditableLine[]): boolean {
  return lines
    .filter((line) => line.title.trim())
    .every(
      (line) =>
        line.type === "text" ||
        (isNumber(line.quantity) &&
          isNumber(line.unitPrice || "0") &&
          (!line.discountPercent || isNumber(line.discountPercent))),
    );
}

export function liveTotals(
  lines: readonly EditableLine[],
  discount: { percent: string; amount: string },
  settings: CommercialSettingsResponse | undefined,
) {
  const valid = lines.filter(
    (line) =>
      line.title.trim() &&
      (line.type === "text" ||
        (isNumber(line.quantity) && isNumber(line.unitPrice || "0"))),
  );
  return calcDocumentTotals(
    valid.map((line) => ({
      type: line.type,
      quantity: line.type === "text" ? 0 : line.quantity,
      unitPrice: line.type === "text" ? 0 : line.unitPrice || 0,
      discountPercent: isNumber(line.discountPercent)
        ? line.discountPercent
        : null,
      taxRate: settings?.vatRegistered === false ? 0 : line.taxRate || 0,
    })),
    {
      documentDiscountPercent: isNumber(discount.percent)
        ? discount.percent
        : null,
      documentDiscountAmount: isNumber(discount.amount)
        ? discount.amount
        : null,
      roundingStep: settings?.roundingStep ?? "0.01",
    },
  );
}

function useCatalog(enabled: boolean) {
  return useQuery({
    queryKey: ["catalog", "picker"],
    queryFn: () =>
      apiGet<{ data: CatalogItemResponse[] }>("/catalog/items", {
        query: { active: true, pageSize: 200 },
      }).then((response) => response.data),
    enabled,
    staleTime: 60_000,
  });
}

export function LineEditor({
  lines,
  onChange,
  currency,
  settings,
  /** Invoices from an offer: lines keep their source and max quantity. */
  maxQuantityBySource,
}: {
  lines: EditableLine[];
  onChange: (lines: EditableLine[]) => void;
  currency: string;
  settings: CommercialSettingsResponse | undefined;
  maxQuantityBySource?: Record<string, string>;
}) {
  const { t, i18n } = useTranslation();
  const { has } = usePermissions();
  const units = useUnits();
  const catalog = useCatalog(has("finance.view"));
  const [catalogId, setCatalogId] = useState("");
  const locale = i18n.resolvedLanguage?.split("-")[0];
  const defaultTax = settings?.defaultTaxRate ?? "0";

  const update = (key: string, patch: Partial<EditableLine>) =>
    onChange(
      lines.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    );
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= lines.length) return;
    const next = [...lines];
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange(next);
  };
  const addCatalogItem = (id: string) => {
    const item = catalog.data?.find((candidate) => candidate.id === id);
    if (!item) return;
    const translation =
      locale === "de" || locale === "fr" || locale === "es"
        ? item.translations[locale]
        : undefined;
    const line: EditableLine = {
      ...emptyLine(defaultTax),
      type: "product",
      title: translation?.name ?? item.name,
      description: translation?.description ?? item.description ?? "",
      unit: "pcs",
      unitPrice: item.unitPrice,
      taxRate: settings?.vatRegistered === false ? "0" : item.taxRate,
      catalogItemId: item.id,
    };
    const onlyBlank = lines.length === 1 && !lines[0]!.title.trim();
    onChange(onlyBlank ? [line] : [...lines, line]);
    setCatalogId("");
  };

  const typeOptions = LINE_ITEM_TYPES.map((value) => ({
    value,
    label: t(`lines.types.${value}`),
  }));
  const unitOptions = (units.data ?? [])
    .filter((unit) => unit.active)
    .map((unit) => ({ value: unit.code, label: units.localizedName(unit) }));
  const taxOptions = (settings?.taxRates ?? []).map((rate) => ({
    value: rate.rate,
    label: `${Number(rate.rate)}% · ${rate.name}`,
  }));
  const lineNets = useMemo(
    () =>
      lines.map((line) => {
        if (line.type === "text") return null;
        if (!isNumber(line.quantity) || !isNumber(line.unitPrice || "0"))
          return null;
        const gross = Number(line.quantity) * Number(line.unitPrice || 0);
        const pct = isNumber(line.discountPercent)
          ? Number(line.discountPercent)
          : 0;
        return gross * (1 - pct / 100);
      }),
    [lines],
  );

  return (
    <div className="space-y-3">
      <ol className="space-y-3">
        {lines.map((line, index) => {
          const text = line.type === "text";
          const max = line.sourceOfferItemId
            ? maxQuantityBySource?.[line.sourceOfferItemId]
            : undefined;
          const overMax =
            max !== undefined &&
            isNumber(line.quantity) &&
            Number(line.quantity) > Number(max);
          return (
            <li
              key={line.key}
              className="rounded-xl border border-app-border bg-app-surface p-3"
            >
              <div className="mb-2 flex items-center gap-2">
                <span className="w-6 text-sm font-semibold text-app-muted">
                  {index + 1}
                </span>
                <div className="w-40">
                  <SelectInput
                    value={line.type}
                    onChange={(type) =>
                      update(line.key, {
                        type,
                        ...(type === "discount" &&
                        line.unitPrice &&
                        !line.unitPrice.startsWith("-")
                          ? { unitPrice: `-${line.unitPrice}` }
                          : {}),
                      })
                    }
                    options={typeOptions}
                    ariaLabel={t("lines.type")}
                    disabled={Boolean(line.sourceOfferItemId)}
                  />
                </div>
                {line.sourceOfferItemId ? (
                  <span className="text-xs text-app-muted">
                    {t("lines.fromOffer")}
                  </span>
                ) : null}
                <div className="ml-auto flex gap-1">
                  <Button
                    type="button"
                    size="small"
                    variant="text"
                    severity="secondary"
                    icon="pi pi-arrow-up"
                    aria-label={t("common.moveUp")}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  />
                  <Button
                    type="button"
                    size="small"
                    variant="text"
                    severity="secondary"
                    icon="pi pi-arrow-down"
                    aria-label={t("common.moveDown")}
                    disabled={index === lines.length - 1}
                    onClick={() => move(index, 1)}
                  />
                  <Button
                    type="button"
                    size="small"
                    variant="text"
                    severity="danger"
                    icon="pi pi-trash"
                    aria-label={t("common.remove")}
                    onClick={() =>
                      onChange(
                        lines.length === 1
                          ? [emptyLine(defaultTax)]
                          : lines.filter((other) => other.key !== line.key),
                      )
                    }
                  />
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-12">
                <div className="md:col-span-12">
                  <InputText
                    value={line.title}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      update(line.key, { title: event.target.value })
                    }
                    placeholder={
                      text ? t("lines.textPlaceholder") : t("lines.title")
                    }
                    aria-label={t("lines.title")}
                    className="w-full font-medium"
                  />
                </div>
                <div className="md:col-span-12">
                  <Textarea
                    value={line.description}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                      update(line.key, { description: event.target.value })
                    }
                    placeholder={t("lines.description")}
                    aria-label={t("lines.description")}
                    rows={1}
                    autoResize
                    className="w-full text-sm"
                  />
                </div>
                {text ? null : (
                  <>
                    <Field
                      label={t("lines.quantity")}
                      className="md:col-span-2"
                      error={
                        overMax
                          ? t("lines.maxQuantity", { max: Number(max) })
                          : undefined
                      }
                    >
                      <InputText
                        inputMode="decimal"
                        value={line.quantity}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          update(line.key, {
                            quantity: event.target.value.replace(",", "."),
                          })
                        }
                        invalid={!isNumber(line.quantity) || overMax}
                      />
                    </Field>
                    <Field label={t("lines.unit")} className="md:col-span-2">
                      <SelectInput
                        value={line.unit}
                        onChange={(unit) => update(line.key, { unit })}
                        options={
                          unitOptions.some((o) => o.value === line.unit)
                            ? unitOptions
                            : [
                                ...unitOptions,
                                { value: line.unit, label: line.unit },
                              ]
                        }
                      />
                    </Field>
                    <Field
                      label={t("lines.unitPrice", { currency })}
                      className="md:col-span-3"
                    >
                      <InputText
                        inputMode="decimal"
                        value={line.unitPrice}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          update(line.key, {
                            unitPrice: event.target.value.replace(",", "."),
                          })
                        }
                        placeholder="0.00"
                        invalid={
                          Boolean(line.unitPrice) && !isNumber(line.unitPrice)
                        }
                      />
                    </Field>
                    <Field
                      label={t("lines.discountPercent")}
                      className="md:col-span-2"
                    >
                      <InputText
                        inputMode="decimal"
                        value={line.discountPercent}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          update(line.key, {
                            discountPercent: event.target.value.replace(
                              ",",
                              ".",
                            ),
                          })
                        }
                        placeholder="0"
                      />
                    </Field>
                    {settings?.vatRegistered === false ? null : (
                      <Field label={t("lines.tax")} className="md:col-span-3">
                        <SelectInput
                          value={line.taxRate}
                          onChange={(taxRate) => update(line.key, { taxRate })}
                          options={
                            taxOptions.some((o) => o.value === line.taxRate)
                              ? taxOptions
                              : [
                                  ...taxOptions,
                                  {
                                    value: line.taxRate,
                                    label: `${Number(line.taxRate)}%`,
                                  },
                                ]
                          }
                        />
                      </Field>
                    )}
                  </>
                )}
              </div>
              {lineNets[index] !== null && lineNets[index] !== undefined ? (
                <p className="mt-2 text-right text-sm tabular-nums text-app-muted">
                  {t("lines.lineTotal")}{" "}
                  <span className="font-semibold text-app-text">
                    {formatMoney(lineNets[index]!, currency)}
                  </span>
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Button
          type="button"
          variant="outlined"
          icon="pi pi-plus"
          onClick={() => onChange([...lines, emptyLine(defaultTax)])}
        >
          {t("lines.add")}
        </Button>
        <Button
          type="button"
          variant="text"
          severity="secondary"
          icon="pi pi-align-left"
          onClick={() =>
            onChange([...lines, { ...emptyLine(defaultTax), type: "text" }])
          }
        >
          {t("lines.addText")}
        </Button>
        {catalog.data && catalog.data.length > 0 ? (
          <div className="sm:ml-auto sm:w-80">
            <SelectInput
              value={catalogId}
              onChange={addCatalogItem}
              filter
              placeholder={t("lines.addFromCatalog")}
              ariaLabel={t("lines.addFromCatalog")}
              options={catalog.data.map((item) => ({
                value: item.id,
                label: `${item.sku ? `${item.sku} · ` : ""}${item.name} · ${formatMoney(item.unitPrice, currency)}`,
              }))}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Read-only line table for issued/sent documents. */
export function LineTable({
  items,
  currency,
  showTax,
}: {
  items: readonly CommercialLineResponse[];
  currency: string;
  showTax: boolean;
}) {
  const { t } = useTranslation();
  const units = useUnits();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-app-border text-left text-xs uppercase tracking-wide text-app-muted">
          <tr>
            <th className="py-2 pr-2">#</th>
            <th className="py-2 pr-2">{t("lines.title")}</th>
            <th className="py-2 pr-2 text-right">{t("lines.quantity")}</th>
            <th className="hidden py-2 pr-2 text-right sm:table-cell">
              {t("lines.unitPriceShort")}
            </th>
            {showTax ? (
              <th className="hidden py-2 pr-2 text-right sm:table-cell">
                {t("lines.tax")}
              </th>
            ) : null}
            <th className="py-2 text-right">{t("lines.lineTotal")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-app-border">
          {items.map((item, index) => (
            <tr key={item.id} className="align-top">
              <td className="py-2 pr-2 text-app-muted">
                {item.type === "text" ? "" : index + 1}
              </td>
              <td className="py-2 pr-2">
                <div
                  className={cx(
                    "font-medium",
                    item.type === "text" && "font-normal italic",
                  )}
                >
                  {item.title}
                </div>
                {item.description ? (
                  <div className="whitespace-pre-line text-app-muted">
                    {item.description}
                  </div>
                ) : null}
                {item.discountPercent ? (
                  <div className="text-xs text-app-muted">
                    {t("lines.discount")} −{Number(item.discountPercent)}%
                  </div>
                ) : null}
              </td>
              <td className="whitespace-nowrap py-2 pr-2 text-right tabular-nums">
                {item.type === "text"
                  ? ""
                  : `${Number(item.quantity)} ${units.formatUnit(item.unit)}`}
              </td>
              <td className="hidden whitespace-nowrap py-2 pr-2 text-right tabular-nums sm:table-cell">
                {item.type === "text"
                  ? ""
                  : formatMoney(item.unitPrice, currency)}
              </td>
              {showTax ? (
                <td className="hidden py-2 pr-2 text-right tabular-nums sm:table-cell">
                  {item.type === "text" ? "" : `${Number(item.taxRate)}%`}
                </td>
              ) : null}
              <td className="whitespace-nowrap py-2 text-right font-medium tabular-nums">
                {item.type === "text"
                  ? ""
                  : formatMoney(item.lineTotal, currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type TotalsLike = Pick<
  CommercialTotalsResponse,
  | "subtotal"
  | "documentDiscount"
  | "netExclTax"
  | "taxTotal"
  | "roundingAdjustment"
  | "grandTotal"
> & {
  taxBreakdown: ReadonlyArray<{ rate: string; net: string; tax: string }>;
};

export function toTotalsLike(
  totals: ReturnType<typeof calcDocumentTotals>,
): TotalsLike {
  return {
    subtotal: totals.subtotal.toFixed(2),
    documentDiscount: totals.documentDiscount.toFixed(2),
    netExclTax: totals.netExclTax.toFixed(2),
    taxTotal: totals.taxTotal.toFixed(2),
    roundingAdjustment: totals.roundingAdjustment.toFixed(2),
    grandTotal: totals.grandTotal.toFixed(2),
    taxBreakdown: totals.taxBreakdown.map((entry) => ({
      rate: entry.rate.toString(),
      net: entry.net.toFixed(2),
      tax: entry.tax.toFixed(2),
    })),
  };
}

export function TotalsPanel({
  totals,
  currency,
  showTax,
  totalLabel,
}: {
  totals: TotalsLike;
  currency: string;
  showTax: boolean;
  totalLabel?: string;
}) {
  const { t } = useTranslation();
  const row = (label: string, value: string, strong = false) => (
    <div
      key={label}
      className={cx(
        "flex justify-between gap-4 tabular-nums",
        strong && "border-t border-app-border pt-2 text-base font-semibold",
      )}
    >
      <dt className={strong ? "" : "text-app-muted"}>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
  const hasDiscount = Number(totals.documentDiscount) !== 0;
  return (
    <dl className="space-y-1.5 text-sm">
      {row(t("totals.subtotal"), formatMoney(totals.subtotal, currency))}
      {hasDiscount
        ? row(
            t("totals.discount"),
            `−${formatMoney(totals.documentDiscount, currency)}`,
          )
        : null}
      {showTax ? (
        <>
          {hasDiscount
            ? row(t("totals.net"), formatMoney(totals.netExclTax, currency))
            : null}
          {totals.taxBreakdown.map((entry) =>
            row(
              t("totals.vatAt", { rate: Number(entry.rate) }),
              formatMoney(entry.tax, currency),
            ),
          )}
        </>
      ) : null}
      {Number(totals.roundingAdjustment) !== 0
        ? row(
            t("totals.rounding"),
            formatMoney(totals.roundingAdjustment, currency),
          )
        : null}
      {row(
        totalLabel ?? t("totals.total"),
        formatMoney(totals.grandTotal, currency),
        true,
      )}
    </dl>
  );
}

/** Document-level discount inputs (percent and/or fixed amount). */
export function DiscountFields({
  percent,
  amount,
  onChange,
  currency,
}: {
  percent: string;
  amount: string;
  onChange: (value: { percent: string; amount: string }) => void;
  currency: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label={t("totals.discountPercent")}>
        <InputText
          inputMode="decimal"
          value={percent}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange({ percent: event.target.value.replace(",", "."), amount })
          }
          placeholder="0"
        />
      </Field>
      <Field label={t("totals.discountAmount", { currency })}>
        <InputText
          inputMode="decimal"
          value={amount}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange({ percent, amount: event.target.value.replace(",", ".") })
          }
          placeholder="0.00"
        />
      </Field>
    </div>
  );
}
